import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Award, Download, Calendar, Loader2, ExternalLink } from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };

const LearnerCertificatesPage = () => {
  const { user } = useAuth();
  const variant = user?.role === "manager" ? "manager" : "learner";
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [viewing, setViewing] = useState(null);
  const linkRef = useRef(null);

  useEffect(() => {
    api.get("/my/certificates").then(r => setCerts(r.data || [])).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleDownload = async (cert, idx) => {
    setDownloading(idx);
    try {
      const res = await api.get(`/enrollments/${cert.enrollment_id}/certificate`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Use hidden anchor element for reliable download
      const a = linkRef.current || document.createElement("a");
      a.href = url;
      a.download = `Certificate_${cert.course_title.replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 200);
      
      toast.success("Certificate downloaded!");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download certificate");
    } finally { setDownloading(null); }
  };

  const handleView = async (cert, idx) => {
    setViewing(idx);
    // Open blank tab immediately (before async) to avoid popup blocker
    const newTab = window.open("about:blank", "_blank");
    try {
      const res = await api.get(`/enrollments/${cert.enrollment_id}/certificate?view=inline`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      if (newTab) {
        newTab.location.href = url;
      } else {
        // Fallback: download instead
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.download = `Certificate_${cert.course_title.replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => window.URL.revokeObjectURL(url), 200);
      }
    } catch (err) {
      console.error("View error:", err);
      if (newTab) newTab.close();
      toast.error("Failed to open certificate");
    } finally { setViewing(null); }
  };

  if (loading) return (
    <AdminLayout title="My Certificates" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="My Certificates" variant={variant}>
      <motion.div className="space-y-5" variants={container} initial="hidden" animate="show" data-testid="certificates-page">
        <motion.div variants={item}>
          <h1 className="text-2xl font-extrabold tracking-tight">My Certificates</h1>
          <p className="text-sm text-muted-foreground mt-1">{certs.length} certificate{certs.length !== 1 ? "s" : ""} earned</p>
        </motion.div>
        {certs.length === 0 ? (
          <motion.div variants={item}>
            <Card className="border shadow-none"><CardContent className="p-12 text-center text-muted-foreground"><Award className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="font-medium">No certificates yet</p><p className="text-xs mt-1">Complete courses to earn certificates</p></CardContent></Card>
          </motion.div>
        ) : (
          <motion.div variants={item} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {certs.map((cert, i) => (
              <Card key={cert.id} className="border shadow-none hover:shadow-md transition-shadow overflow-hidden" data-testid={`certificate-${i}`}>
                <div className="h-36 relative overflow-hidden" style={{
                  background: "linear-gradient(135deg, #0B1D2E 0%, #0F3A3F 50%, #14564D 100%)"
                }}>
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-2 left-2 w-16 h-16 border border-amber-400/30 rounded-full" />
                    <div className="absolute bottom-2 right-2 w-20 h-20 border border-amber-400/20 rounded-full" />
                  </div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                    <div className="w-8 h-8 rounded-full bg-amber-400/20 border border-amber-400/40 flex items-center justify-center mb-2">
                      <Award className="w-4 h-4 text-amber-400" />
                    </div>
                    <p className="text-amber-400/80 text-[9px] font-semibold tracking-[0.25em] uppercase">Certificate of Completion</p>
                    <p className="text-white font-bold text-sm mt-1.5 text-center line-clamp-2 px-4">{cert.course_title}</p>
                    <div className="w-16 h-[1px] bg-amber-400/40 mt-2" />
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    {cert.category && <Badge variant="outline" className="text-[10px]">{cert.category}</Badge>}
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      <span>{cert.completed_at ? new Date(cert.completed_at).toLocaleDateString() : "N/A"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline" size="sm"
                      className="text-xs h-8 gap-1.5 flex-1"
                      onClick={() => handleView(cert, i)}
                      disabled={viewing === i}
                      data-testid={`view-cert-${i}`}
                    >
                      {viewing === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                      View
                    </Button>
                    <Button
                      variant="default" size="sm"
                      className="text-xs h-8 gap-1.5 flex-1"
                      onClick={() => handleDownload(cert, i)}
                      disabled={downloading === i}
                      data-testid={`download-cert-${i}`}
                    >
                      {downloading === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </motion.div>
        )}
      </motion.div>
    </AdminLayout>
  );
};

export default LearnerCertificatesPage;
